import {
  Edge,
  getNodesBounds,
  Node,
  ReactFlowInstance,
  Rect,
  XYPosition,
} from "reactflow"
import { AutogenResultType, UIEdge, UILogic, UISheet } from "../types/UITypes"
import { CANVAS_SIZE } from "./constants"
import { getClusters } from "./2dsheet"

const columnWidth = 225
const rowHeight = 40
const headerHeight = 40
const verticalGap = 50
const interSheetsGap = 2 * columnWidth

const distance = (x1: number, y1: number, x2: number, y2: number) => {
  return Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2))
}

export const arrangeAutogenSheets = (
  autogenResult: AutogenResultType,
  reactFlow: ReactFlowInstance
) => {
  const { importedSheets, importedEdges, importedLogics } = autogenResult
  const sheetsById: Record<string, Node<UISheet>> = {}
  const logicsById: Record<string, Node<UILogic>> = {}
  const sheetsSizeById: Record<string, { width: number; height: number }> = {}
  const incomingEdgeIdsBySheetIds: Record<string, string[]> = {}
  const nodes = reactFlow.getNodes()
  const edges = reactFlow.getEdges()
  const apiClusters = getClusters(nodes, edges)

  importedEdges.forEach((edge) => {
    const sheetId = edge.data?.targetSheetId
    if (sheetId) {
      if (!incomingEdgeIdsBySheetIds[sheetId]) {
        incomingEdgeIdsBySheetIds[sheetId] = []
      }

      incomingEdgeIdsBySheetIds[sheetId].push(edge.data?.id!)
    }
  })

  importedLogics.forEach((logic) => {
    logicsById[logic.data.id] = logic
  })

  importedSheets.forEach((sheet) => {
    sheetsById[sheet.data.id] = sheet

    let sheetRows = 1
    let sheetColumns = 1

    sheet.data.cells.forEach((cell) => {
      if (cell.rowIndex > sheetRows) {
        sheetRows = cell.rowIndex
      }

      if (cell.columnIndex > sheetColumns) {
        sheetColumns = cell.columnIndex
      }
    })

    // sheetRows + 1 is used to calculate the total height of sheet rows + height of column label
    sheetsSizeById[sheet.data.id] = {
      width: sheetColumns * columnWidth,
      height: (sheetRows + 1) * rowHeight + headerHeight + verticalGap,
    }
  })

  const sheetsWithoutOutgoingEdges = importedSheets.filter((sheet) => {
    return !importedEdges.some(
      (edge) => edge.data?.sourceSheetId === sheet.data?.id
    )
  })

  const numIncomingEdgesPerSheet: Record<string, number> = {}

  importedEdges.forEach((edge) => {
    if (edge.data?.targetSheetId) {
      if (!numIncomingEdgesPerSheet[edge.data?.targetSheetId]) {
        numIncomingEdgesPerSheet[edge.data?.targetSheetId] = 1
      } else {
        numIncomingEdgesPerSheet[edge.data?.targetSheetId]++
      }
    }
  })

  sheetsWithoutOutgoingEdges.sort(
    (a, b) =>
      numIncomingEdgesPerSheet[a.data.id] - numIncomingEdgesPerSheet[b.data.id]
  )

  const baseCoords = reactFlow.project({
    x: window.innerWidth,
    y: window.innerHeight * 0.5,
  }) ?? { x: 0, y: 0 }

  let sheetsColumnHeight: number = 0
  let sheetsColumnWidth: number = 0

  sheetsWithoutOutgoingEdges.forEach((sheet) => {
    const sheetWidth = sheetsSizeById[sheet.data.id].width
    sheetsColumnHeight += sheetsSizeById[sheet.data.id].height

    if (sheetWidth > sheetsColumnWidth) {
      sheetsColumnWidth = sheetWidth
    }
  })

  const screenCenterY = baseCoords.y
  let currentY = screenCenterY - sheetsColumnHeight / 2
  let currentX = baseCoords.x - sheetsColumnWidth - 50 // leave a 50px gap to the side of the screen

  sheetsWithoutOutgoingEdges.forEach((sheet) => {
    const importedSheet = sheetsById[sheet.data.id]

    importedSheet.position = {
      x: currentX,
      y: currentY,
    }
    importedSheet.data.x = currentX
    importedSheet.data.y = currentY

    currentY += sheetsSizeById[sheet.data.id].height
  })

  arrangeSheets(
    sheetsWithoutOutgoingEdges,
    sheetsById,
    sheetsSizeById,
    logicsById,
    autogenResult,
    screenCenterY,
    incomingEdgeIdsBySheetIds
  )

  importedLogics.forEach((logic) => {
    const logicSources: { sheet: Node<UISheet>; rowIndex: number }[] = []

    importedEdges.forEach((edge) => {
      if (edge.data?.targetLogicId === logic.data.id) {
        logicSources.push({
          sheet: sheetsById[edge.data?.sourceSheetId!],
          rowIndex: edge.data?.sourceRowIndex!,
        })
      }
    })

    let sourceSheetsColumnRight = 0
    const sourceRowsY: number[] = []
    logicSources.forEach(({ sheet, rowIndex }) => {
      const sheetRight = sheet.position.x + sheetsSizeById[sheet.data.id].width

      if (sheetRight > sourceSheetsColumnRight) {
        sourceSheetsColumnRight = sheetRight
      }

      sourceRowsY.push(
        sheet.position.y + (rowIndex + 1) * rowHeight + headerHeight
      )
    })
    const targetSheetsColumnLeft = sourceSheetsColumnRight + columnWidth * 2

    logic.position.x =
      sourceSheetsColumnRight +
      (targetSheetsColumnLeft - sourceSheetsColumnRight) / 2

    logic.position.y = sourceRowsY.reduce((a, b) => a + b) / sourceRowsY.length
  })

  const topLeft = reactFlow.screenToFlowPosition({ x: 0, y: 0 })

  const bottomRight = reactFlow.screenToFlowPosition({
    x: window.innerWidth,
    y: window.innerHeight,
  })

  const screenCenter = {
    x: topLeft.x + (bottomRight.x - topLeft.x) / 2,
    y: topLeft.y + (bottomRight.y - topLeft.y) / 2,
  }

  const importedSheetsBounds = getNodesBounds(importedSheets)

  const centeredSheetsCellX = Math.round(
    (screenCenter.x - importedSheetsBounds.width / 2) / interSheetsGap
  )
  const centeredSheetsCellY = Math.round(
    (screenCenter.y - importedSheetsBounds.height / 2) / interSheetsGap
  )

  const numCanvasCells = Math.floor(CANVAS_SIZE / interSheetsGap)
  const maxCanvasCellX =
    numCanvasCells - Math.round(importedSheetsBounds.width / interSheetsGap)
  const maxCanvasCellY =
    numCanvasCells - Math.round(importedSheetsBounds.height / interSheetsGap)

  const getRelativeCoords = (pos: XYPosition) => ({
    x: Math.max(pos.x - importedSheetsBounds.x, 0),
    y: Math.max(pos.y - importedSheetsBounds.y, 0),
  })

  importedSheets.forEach((sheet) => {
    sheet.position = getRelativeCoords(sheet.position)

    sheet.data.x = sheet.position.x
    sheet.data.y = sheet.position.y
  })

  importedLogics.forEach((logic) => {
    logic.position = getRelativeCoords(logic.position)
  })

  let autopositioningComplete = false
  let currentStep = 0

  const clustersBounds: Rect[] = apiClusters.map((nodes) =>
    getNodesBounds(nodes)
  )

  const hasIntersections = (rect1: Rect, rect2: Rect) => {
    return (
      (rect1.x < rect2.x + rect2.width &&
        rect1.x + rect1.width > rect2.x &&
        rect1.y < rect2.y + rect2.height &&
        rect1.y + rect1.height > rect2.y) ||
      (rect1.x >= rect2.x &&
        rect1.x + rect1.width <= rect2.x + rect2.width &&
        rect1.y >= rect2.y &&
        rect1.y + rect1.height <= rect2.y + rect2.height) ||
      (rect2.x >= rect1.x &&
        rect2.x + rect2.width <= rect1.x + rect1.width &&
        rect2.y >= rect1.y &&
        rect2.y + rect2.height <= rect1.y + rect1.height)
    )
  }

  const intersectsCluster = (rect: Rect) => {
    return clustersBounds.some((clusterBounds) => {
      return hasIntersections(rect, clusterBounds)
    })
  }

  const hasFreeSpace = (x: number, y: number) => {
    const rectX = x * interSheetsGap
    const rectY = y * interSheetsGap
    const rect = {
      x: rectX,
      y: rectY,
      width: importedSheetsBounds.width,
      height: importedSheetsBounds.height,
    }

    return !intersectsCluster(rect)
  }

  const freeSpaceSlots = []

  while (!autopositioningComplete) {
    const left = Math.max(centeredSheetsCellX - currentStep, 0)
    const hasLeftSide = centeredSheetsCellX - currentStep >= 0
    const top = Math.max(centeredSheetsCellY - currentStep, 0)
    const hasTopSide = centeredSheetsCellY - currentStep >= 0
    const right = Math.min(centeredSheetsCellX + currentStep, maxCanvasCellX)
    const hasRightSide = centeredSheetsCellX + currentStep <= maxCanvasCellX
    const bottom = Math.min(centeredSheetsCellY + currentStep, maxCanvasCellY)
    const hasBottomSide = centeredSheetsCellY + currentStep <= maxCanvasCellY

    if (!(hasTopSide || hasRightSide || hasBottomSide || hasLeftSide)) {
      autopositioningComplete = true
    }

    if (hasTopSide) {
      for (let x = left; x <= right; x++) {
        if (hasFreeSpace(x, top)) {
          freeSpaceSlots.push({ x: x, y: top })
        }
      }
    }

    if (hasRightSide) {
      for (let y = top; y <= bottom; y++) {
        if (hasFreeSpace(right, y)) {
          freeSpaceSlots.push({ x: right, y: y })
        }
      }
    }

    if (hasBottomSide) {
      for (let x = left; x <= right; x++) {
        if (hasFreeSpace(x, bottom)) {
          freeSpaceSlots.push({ x: x, y: bottom })
        }
      }
    }

    if (hasLeftSide) {
      for (let y = top; y <= bottom; y++) {
        if (hasFreeSpace(left, y)) {
          freeSpaceSlots.push({ x: left, y: y })
        }
      }
    }

    currentStep++
  }

  let freeSpaceTopLeft = {
    x: centeredSheetsCellX,
    y: centeredSheetsCellY,
  }

  if (freeSpaceSlots.length) {
    let minDistance: number

    freeSpaceSlots.forEach(({ x, y }) => {
      const currentDistance = distance(
        x,
        y,
        centeredSheetsCellX,
        centeredSheetsCellY
      )

      if (currentDistance < minDistance || minDistance === undefined) {
        minDistance = currentDistance
        freeSpaceTopLeft = { x, y }
      }
    })
  }

  const importedSheetsX = interSheetsGap * freeSpaceTopLeft.x
  const importedSheetsY = interSheetsGap * freeSpaceTopLeft.y

  importedSheets.forEach((sheet) => {
    sheet.position.x += importedSheetsX
    sheet.position.y += importedSheetsY

    sheet.data.x = sheet.position.x
    sheet.data.y = sheet.position.y
  })

  importedLogics.forEach((logic) => {
    logic.position.x += importedSheetsX
    logic.position.y += importedSheetsY
  })

  importedSheetsBounds.x = importedSheetsX
  importedSheetsBounds.y = importedSheetsY

  reactFlow.fitBounds(
    {
      ...importedSheetsBounds,
      x: interSheetsGap * freeSpaceTopLeft.x,
      y: interSheetsGap * freeSpaceTopLeft.y,
    },
    { duration: 500, padding: 0.25 }
  )

  return {
    importedSheets,
    importedEdges,
    importedLogics,
  }
}

function arrangeSheets(
  prevColumnSheets: Node<UISheet>[],
  sheetsById: Record<string, Node<UISheet>>,
  sheetsSizeById: Record<string, { width: number; height: number }>,
  logicsById: Record<string, Node<UILogic>>,
  autogenResult: AutogenResultType,
  screenCenterY: number,
  incomingEdgeIdsBySheetIds: Record<string, string[]>
) {
  const sheetsColumn: Node<UISheet>[] = []
  const { importedEdges } = autogenResult

  let incomingEdges: Edge<UIEdge>[] = []

  prevColumnSheets.forEach((sheet) => {
    incomingEdges = incomingEdges.concat(
      importedEdges.filter(
        (edge) => edge.data?.targetSheetId === sheet.data?.id
      )
    )
  })

  if (!incomingEdges?.length) {
    return
  }

  const sheetsByPrevColumnSheetsIncomingEdges: Record<string, Node<UISheet>[]> =
    {}

  incomingEdges.forEach((edge) => {
    if (edge.data?.sourceSheetId) {
      const sheet = sheetsById[edge.data?.sourceSheetId]

      if (!sheetsColumn.includes(sheet)) {
        sheetsColumn.push(sheet)
      }
      sheetsByPrevColumnSheetsIncomingEdges[edge.data?.id] = [sheet]
    }

    if (edge.data?.sourceLogicId) {
      const logic = logicsById[edge.data?.sourceLogicId]
      const edgeId = edge.data?.id
      sheetsByPrevColumnSheetsIncomingEdges[edgeId] = []

      importedEdges
        .filter((edge) => edge.data?.targetLogicId === logic.data.id)
        .forEach((edge) => {
          if (edge.data?.sourceSheetId) {
            const sheet = sheetsById[edge.data?.sourceSheetId]

            if (!sheetsColumn.includes(sheet)) {
              sheetsColumn.push(sheet)
            }
            sheetsByPrevColumnSheetsIncomingEdges[edgeId].push(sheet)
          }
        })
    }
  })

  let sheetsColumnHeight: number = 0
  let sheetsColumnWidth: number = 0

  sheetsColumn.forEach((sheet) => {
    const sheetWidth = sheetsSizeById[sheet.data.id].width
    sheetsColumnHeight += sheetsSizeById[sheet.data.id].height

    if (sheetWidth > sheetsColumnWidth) {
      sheetsColumnWidth = sheetWidth
    }
  })

  let currentY = screenCenterY - sheetsColumnHeight / 2
  let currentX =
    prevColumnSheets[0].data.x - sheetsColumnWidth - columnWidth * 2

  const processedSheets: Record<string, boolean> = {}

  prevColumnSheets.forEach((sheet) => {
    const prevColumnSheetsIncomingEdges =
      incomingEdgeIdsBySheetIds[sheet.data.id]

    prevColumnSheetsIncomingEdges?.forEach((edgeId: string) => {
      const sheets = sheetsByPrevColumnSheetsIncomingEdges[edgeId]

      sheets.forEach((sheet) => {
        if (!processedSheets[sheet.data.id]) {
          processedSheets[sheet.data.id] = true
          const importedSheet = sheetsById[sheet.data.id]

          importedSheet.position = {
            x: currentX,
            y: currentY,
          }
          importedSheet.data.x = currentX
          importedSheet.data.y = currentY

          currentY += sheetsSizeById[sheet.data.id].height
        }
      })
    })
  })

  if (!sheetsColumn) {
    return
  }

  // TODO: check if there are no short-circuiting sheets
  arrangeSheets(
    sheetsColumn,
    sheetsById,
    sheetsSizeById,
    logicsById,
    autogenResult,
    screenCenterY,
    incomingEdgeIdsBySheetIds
  )
}
